/****************************************************
 * Module Name:  VideoUrl.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useState } from "react";
import { DataGridPro, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarExport, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import { Box, Button, TextField, Tooltip } from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import "./../../style/modalstyle.css";
import CommonSnackbar from "../common/commonSnackBar";
import { LoadingButton } from "@mui/lab";
import PacketHistoryModal from "../common/packetHistoryModal";
import NoPermission from "../common/noPermission";
import Constants from "../../utility/constants";
import { useEffect } from "react";
import Utility from "../../utility/utility";
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import { BackupTable } from "@mui/icons-material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import Papa from 'papaparse';
import PacketProxy from "../../utility/proxy/manager/manufacturingProxy/packetProxy";
import PageTitle from "../common/pageTitle";

const VideoUrl = () => {
    const { apiRef, handleCellClick } = Utility.useSingleClickEdit();
    const [data, setData] = useState([]);
    const [detailLoader, setDetailLoader] = useState(false);
    const [refresh, setRefresh] = useState(false);
    const [alertSnackbar, setAlertSnackbar] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertVariant, setAlertVariant] = useState("");
    const [kapanId, setkapanId] = useState("");
    const [access, setAccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const [packetId, setPacketId] = useState("");
    const [showPacketDetailModal, setShowPacketDetailModal] = useState(false);
    const [uploadLoader, setUploadLoader] = useState(false);

    useEffect(() => {
        PageTitle("MANUFACTURING_OPERATION_ADDVIDEOURL");
        checkPermission();
    }, []);

    useEffect(() => {
        if (kapanId.length === 0)
            setData([]);
    }, [kapanId]);

    const checkPermission = () => {
        let allowed = Constants.USER_PERMISSION.find(
            (iPermission) =>
                iPermission.permissionid ===
                Constants.PERMISSIONS.MANUFACTURING_OPERATION_ADDVIDEOURL
        ) !== undefined
        if (!allowed) {
            setAccess(true)
            return <NoPermission />
        }
    }

    const handleSearch = () => {
        if (kapanId.length === 0) {
            return true;
        }
        setDetailLoader(true)
        PacketProxy.GetAllPacketVideo(kapanId, (response) => {
            if (response) {
                const updatedResponse = response.map(iData => ({
                    ...iData,
                    embededkey: iData.kapanid + "_" + iData.packetid
                }));
                setData(updatedResponse)
                setDetailLoader(false)
            } else {
                setData([]);
                setSnackbarState(true, Constants.APiFailErrorMessage, "error");
                setDetailLoader(false)
            }
        })
    }

    const handleKeyPress = (event) => {
        if (event.key === "Enter" && kapanId.length > 0) {
            handleSearch();
        }
    };

    useEffect(() => {
        if (refresh) {
            window.addEventListener("keydown", handleKeyPress);
            return () => {
                window.removeEventListener("keydown", handleKeyPress);
            };
        }
    }, [kapanId, refresh]);

    const setSnackbarState = (show, message, variant) => {
        setAlertSnackbar(show);
        setAlertMessage(message);
        setAlertVariant(variant);
    }

    const prepareData = (array) => {
        if (!array.length || !Array.isArray(array)) return;
        let arr = [];
        array.forEach((item) => {
            if (item.videopath) {
                let obj = {
                    packetid: item.packetid,
                    kapanid: item.kapanid,
                    videopath: item.videopath
                };
                arr.push(obj);
            }
        });
        return arr;
    };

    const handleCreateLot = () => {
        let data = prepareData(rows)
        setLoading(true);
        if (data.length > 0) {
            PacketProxy.AddPacketsVideo(data, (response) => {
                if (response) {
                    handleSearch();
                    setSnackbarState(true, "Video Url Added Successfully", "success");
                } else {
                    setSnackbarState(true, Constants.APiFailErrorMessage, "error");
                }
            })
        }
        setLoading(false);
    };

    const handlePacketDetailModal = (params) => {
        setPacketId(params);
        setShowPacketDetailModal(true);
    };


    const handleClear = () => {
        const fileInput = document.getElementById('file-input');
        fileInput.value = null;
    }

    const handleUpload = (file) => {
        const reader = new FileReader();
        setUploadLoader(true);
        reader.onload = () => {
            const text = reader.result;
            Papa.parse(text, {
                header: true,
                skipEmptyLines: true,
                complete: (result) => {
                    if (result.data.length <= 0) {
                        setSnackbarState(true, `No Data Found`, "warning");
                        return;
                    }
                    let headersFromCSV = Object.keys(result.data[0]);
                    let requiredColumns = ['packetid', 'videopath'];
                    if (headersFromCSV.length === requiredColumns.length) {
                        const newData = result.data;

                        setData((prevList) => {
                            const updatedDataList = prevList.map(item => {
                                const foundItem = newData.find(data => data.packetid === item.packetid);
                                if (foundItem) {
                                    // Only update fields that have values in the CSV
                                    if (foundItem.videopath !== '') item.videopath = foundItem.videopath;
                                }
                                return item;
                            });
                            return updatedDataList;
                        });
                        setUploadLoader(false);
                        setSnackbarState(true, "File loaded Successfully", "success");
                    } else {
                        setSnackbarState(true, "CSV columns do not match table columns.", "error");
                        setUploadLoader(false);
                    }
                },
                error: (error) => {
                    console.error('Error parsing CSV:', error);
                    setSnackbarState(true, "Error parsing CSV", "error");
                    setUploadLoader(false);
                }
            });
        };
        reader.readAsText(file);
    };

    const columnName = [
        {
            field: "packetid",
            headerName: "PACKET",
            headerAlign: "center",
            align: "center",
            width: 230,
            renderCell: (params) => (Utility.NeedToShowPacketDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => handlePacketDetailModal(params.value)}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
        },
        {
            field: "carats",
            headerName: "CARATS",
            headerAlign: "center",
            align: "center",
            width: 230,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.carats)
        },
        {
            field: "price",
            headerName: "PPC",
            headerAlign: "center",
            align: "center",
            width: 230,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.price)
        },
        {
            field: "videopath",
            headerName: "ENTER URL",
            headerAlign: "center",
            type: "string",
            align: "center",
            width: 830,
            editable: true,
            hideable: false,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '5px', marginLeft: '10px' }}>
                        {params.value}
                    </span>
                    <EditIcon style={{ marginLeft: 3, marginBottom: 3, fontSize: '1rem' }} />
                </div>
            ),
            cellClassName: (params) => 'lavenderCell'
        },
    ];

    const handleProcessRowUpdateError = (error) => {
        // console.error(error);
    };

    const getChangedFields = (newRow, oldRow) => {
        const changedFields = [];

        Object.keys(newRow).forEach(field => {
            if (newRow[field] !== oldRow[field]) {
                changedFields.push({
                    id: newRow.embededkey,
                    field: field,
                    value: newRow[field]
                });
            }
        });

        return changedFields;
    };


    const processRowUpdate = (newRow, oldRow) => {
        const changedFeilds = getChangedFields(newRow, oldRow);
        if (changedFeilds.length > 0) {
            const { id, field, value } = changedFeilds[0];

            const updatedListData = rows.map((iData) => {
                if (iData.embededkey === id && value) {
                    if (field === "videopath") {
                        return {
                            ...iData,
                            [field]: value,
                        }
                    }
                }
                return iData;
            })

            setData(updatedListData);
            const updatedRow = updatedListData.find(row => row.embededkey === id);

            return updatedRow;
        } else {
            // No changes detected, return the newRow to trigger exit edit mode
            return { ...newRow };
        }
    };

    const rows = data;
    const columns = columnName;

    const CustomToolbar = () => {
        return (
            <GridToolbarContainer
                sx={{
                    display: "flex",
                    alignItems: "center",
                    marginHorizontal: "5px",
                    "&.MuiDataGrid-toolbarContainer": {
                        padding: "0px 4px !important",
                    },
                }}
            >
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarExport />
                <Box sx={{ flexGrow: 1, display: "block" }} />
                {detailLoader ? (
                    <CircularProgress size="1rem" />
                ) : (
                    <Tooltip title="Reload List">
                        <RefreshOutlinedIcon
                            onClick={() => handleSearch()}
                            color="primary"
                            fontSize="medium"
                            sx={{ cursor: "pointer" }}
                        />
                    </Tooltip>
                )}
                <Tooltip title="Download Sample Video Url Upload">
                    <a
                        href="/demo_excel/SamplePacketVideoUrlUpload.csv"
                        download="SamplePacketVideoUrlUpload.csv"
                        style={{ color: "red", }}
                    >
                        <BackupTable size={25} />
                    </a>
                </Tooltip>

                <Tooltip title="Upload Video Url">
                    <LoadingButton
                        variant="outlined"
                        size="small"
                        component="label"
                        loading={uploadLoader}
                        disabled={data.length === 0}
                        loadingPosition="start"
                        startIcon={<UploadFileIcon size="large" />}
                    >
                        Upload
                        <input
                            id="file-input"
                            hidden
                            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                            multiple
                            type="file"
                            onChange={(e) => handleUpload(e.target.files[0])}
                            onClick={(e) => handleClear()}
                        />
                    </LoadingButton>
                </Tooltip>
                <LoadingButton
                    variant="contained"
                    size="small"
                    loading={loading}
                    loadingPosition="start"
                    startIcon={<SaveIcon />}
                    component="label"
                    disabled={data.length === 0}
                    onClick={handleCreateLot}
                >
                    Save
                </LoadingButton>
                <GridToolbarQuickFilter debounceMs={500} />
            </GridToolbarContainer>
        )
    }

    return (
        <div>
            {
                access ? <NoPermission /> : (
                    <div>
                        <Box>
                            {showPacketDetailModal ? (
                                <PacketHistoryModal
                                    show={showPacketDetailModal}
                                    packetId={packetId}
                                    kapanId={kapanId}
                                    close={() => setShowPacketDetailModal(false)}
                                />
                            ) : null}
                            <CommonSnackbar
                                show={alertSnackbar}
                                message={alertMessage}
                                variant={alertVariant}
                                autoHide={false}
                                title={alertVariant}
                                handleClose={() => setAlertSnackbar(false)}
                            />
                            <Box px={1}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: { xs: "column", sm: "row" },
                                        flexWrap: "wrap",
                                        width: "100%",
                                        alignItems: "flex-start",
                                        gap: 2,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: 2,
                                            flexGrow: 1,
                                            alignItems: "center",
                                        }}
                                    >
                                        <TextField
                                            margin="normal"
                                            label="Kapan Id"
                                            placeholder="Enter Kapan Id"
                                            value={kapanId}
                                            size="small"
                                            onBlur={(e) => setRefresh(false)}
                                            onChange={(e) => {
                                                setRefresh(true);
                                                setkapanId(e.target.value)
                                            }}
                                        />

                                        <LoadingButton
                                            loading={detailLoader}
                                            loadingPosition="start"
                                            variant="contained"
                                            size="medium"
                                            onClick={() => handleSearch()}
                                            disabled={kapanId.length === 0}
                                            startIcon={<SearchOutlinedIcon />}
                                            sx={{ mt: 1 }}

                                        >
                                            Search
                                        </LoadingButton>
                                    </Box>
                                </Box>
                                <Box
                                    sx={{
                                        backgroundColor: '#fff',
                                        color: '#000',
                                        width: "100%",
                                        position: "relative",
                                        "& .MuiTablePagination-selectLabel": {
                                            marginTop: "15px",
                                        },
                                        "& .MuiTablePagination-displayedRows": {
                                            marginTop: "15px",
                                        },
                                        "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                            fontWeight: Constants.defaultFontWeight,
                                        },
                                        "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
                                            color: "#1976d2",
                                            padding: "4px 5px",
                                            borderRadius: "4px",
                                        },
                                        "& .css-cc8tf1": {
                                            fontWeight: Constants.defaultFontWeight,
                                        },
                                        "& .css-znmrd6-MuiButtonBase-root-MuiButton-root": {
                                            color: "#1976d2",
                                            padding: "5px 5px",
                                            borderRadius: "4px",
                                        },
                                        height: "77vh",
                                    }}
                                >
                                    <DataGridPro
                                        disableRowSelectionOnClick={true}
                                        getRowHeight={() => "auto"}
                                        sx={{
                                            '& .MuiDataGrid-columnHeader': {
                                                color: '#fff',
                                                backgroundColor: 'grey',
                                            },
                                            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                                py: "4px",
                                            },
                                            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                                py: "12px",
                                            },
                                            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                                py: "18px",
                                            },

                                            "& .lavenderCell": {
                                                color: "#080808",
                                                backgroundColor: "#D8BFD8",
                                            },
                                            fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                                        }}
                                        processRowUpdate={processRowUpdate}
                                        density="compact"
                                        onProcessRowUpdateError={handleProcessRowUpdateError}
                                        slots={{ toolbar: CustomToolbar }}
                                        rows={rows}
                                        columns={columns}
                                        getRowId={(row) => row.embededkey}
                                        initialState={{
                                            columns: {
                                                columnVisibilityModel: {
                                                    // Hide columns id, the other columns will remain visible
                                                },
                                            },
                                            sorting: {
                                                sortModel: [{ field: "updatedat", sort: "desc" }],
                                            },
                                            pagination: {
                                                paginationModel: Constants.pageSize,
                                            },
                                        }}
                                        pageSizeOptions={Constants.pageSizeOptions}
                                        pagination
                                        loading={detailLoader}
                                        slotProps={{
                                            toolbar: {
                                                showQuickFilter: true,
                                                quickFilterProps: { debounceMs: 500 },
                                            },
                                        }}
                                        apiRef={apiRef}
                                        onCellClick={handleCellClick}
                                        editMode="cell"
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </div>
                )
            }
        </div>
    );
};

export default VideoUrl;
